/**
 * CallAvatar - Shared avatar component for calls
 * Displays user avatar with online status, call type badge, and animations
 */

'use client';

import React from 'react';
import { User, Users, Video, Phone, Globe } from 'lucide-react';
import type { CallAvatarProps } from '../types';

const sizeClasses = {
  sm: 'w-10 h-10',
  md: 'w-20 h-20',
  lg: 'w-32 h-32',
  xl: 'w-40 h-40',
};

const iconSizeClasses = {
  sm: 'w-5 h-5',
  md: 'w-10 h-10',
  lg: 'w-14 h-14',
  xl: 'w-20 h-20',
};

const badgeSizeClasses = {
  sm: 'w-6 h-6 -bottom-0.5 -right-0.5',
  md: 'w-8 h-8 -bottom-1 -right-1',
  lg: 'w-11 h-11 -bottom-1 -right-1',
  xl: 'w-14 h-14 -bottom-2 -right-2',
};

const badgeIconSizes = {
  sm: 'w-3 h-3',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-7 h-7',
};

export function CallAvatar({
  user,
  screenPopData,
  isGroupCall = false,
  isSipCall = false,
  isVideoCall = false,
  size = 'lg',
  showOnlineStatus = false,
  showCallTypeBadge = true,
  animate = false,
  className = '',
}: CallAvatarProps) {
  // Determine avatar source
  const avatarUrl = screenPopData?.contact?.logo_url 
    || screenPopData?.contact?.photo_url 
    || user?.avatar_url;
  
  // Determine call type color
  const callTypeColor = isVideoCall ? 'blue' : isSipCall ? 'purple' : 'emerald';
  
  // Get call type icon
  const CallTypeIcon = isVideoCall ? Video : isSipCall ? Globe : Phone;
  
  return (
    <div className={`relative ${className}`}>
      {/* Animated rings for incoming calls */}
      {animate && (
        <>
          <div 
            className={`absolute -inset-6 rounded-full animate-ping opacity-10 bg-${callTypeColor}-500`}
            style={{ animationDuration: '2s' }}
          />
          <div 
            className={`absolute -inset-4 rounded-full animate-ping opacity-15 bg-${callTypeColor}-500`}
            style={{ animationDuration: '2s', animationDelay: '0.5s' }}
          />
          <div className={`absolute -inset-2 rounded-full animate-pulse opacity-20 bg-${callTypeColor}-500`} />
          
          {/* Glowing ring */}
          <div 
            className={`absolute -inset-1 rounded-full opacity-60 blur-sm animate-pulse ${
              isVideoCall ? 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600' :
              isSipCall ? 'bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600' :
              'bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600'
            }`}
            style={{ animationDuration: '1.5s' }}
          />
        </>
      )}
      
      {/* Avatar container */}
      <div 
        className={`relative ${sizeClasses[size]} rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center overflow-hidden ring-[3px] shadow-2xl ${
          isVideoCall ? 'ring-blue-400/70 shadow-blue-500/20' : 
          isSipCall ? 'ring-purple-400/70 shadow-purple-500/20' : 
          'ring-emerald-400/70 shadow-emerald-500/20'
        }`}
      >
        {isGroupCall ? (
          <Users className={`${iconSizeClasses[size]} text-blue-300`} />
        ) : avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- call avatars render arbitrary remote contact/user photos in a shared realtime component
          <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
        ) : isSipCall ? (
          <Globe className={`${iconSizeClasses[size]} text-purple-300`} />
        ) : isVideoCall ? (
          <Video className={`${iconSizeClasses[size]} text-blue-300`} />
        ) : (
          <User className={`${iconSizeClasses[size]} text-slate-400`} />
        )}
      </div>
      
      {/* Online status indicator */}
      {showOnlineStatus && user && (
        <span 
          className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-slate-900 ${
            user.is_online ? 'bg-green-500' : 'bg-gray-400'
          }`}
        />
      )}
      
      {/* Call type badge */}
      {showCallTypeBadge && (
        <div className={`absolute ${badgeSizeClasses[size]} rounded-full flex items-center justify-center shadow-xl ring-4 ring-slate-900 ${
          isVideoCall ? 'bg-gradient-to-br from-blue-400 to-blue-600' : 
          isSipCall ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 
          'bg-gradient-to-br from-emerald-400 to-emerald-600'
        }`}>
          <CallTypeIcon className={`${badgeIconSizes[size]} text-white`} />
        </div>
      )}
    </div>
  );
}

export default CallAvatar;
